<template>
      <v-container class="d-flex flex-column">
          <div class="form align-self-center">
            <form v-on:submit.prevent="signIn()">
              <h2 class="text-h5 font-weight-bold">Welcome to GUIOMENTOR</h2>
              <h5 class="subtitle-1">Let's start with a few simple steps</h5>
              <div class="mt-5">
                <v-progress-linear v-if="loading" indeterminate/>
                <v-text-field
                    v-model="email"
                    :error-messages="emailErrors"
                    data-testid="v-text-field"
                    label="Email"
                    @input="emailOnChange()"
                />
                <v-text-field
                    v-model="password"
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    :error-messages="passwordErrors"
                    :type="showPassword ? 'text' : 'password'"
                    data-testid="v-text-field"
                    label="Password"
                    @click:append="showPassword = !showPassword"
                />
              </div>
              <v-row class="my-0">
                <v-col class="d-flex flex-row justify-space-between mx-0 px-0 my-0">
                  <v-checkbox
                      class="text-body-2 my-0 py-0 text-no-wrap"
                      dense
                      label="Remember me"
                      off-icon="mdi-checkbox-blank-circle-outline"
                      on-icon="mdi-check-circle-outline"
                  />
                  <a
                      class="text-decoration-none text-body-2 forgot-password text-no-wrap"
                      @click.prevent="$router.push('/forgot_password')"
                  >Forgot password?
                  </a>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="d-flex flex-row justify-space-between mx-0 my-0 px-0 py-0">
                  <v-btn
                      :disabled="$v.$invalid || loading"
                      class="btn-text"
                      color="primary"
                      data-testid="login-btn"
                      type="submit"
                      width="46%"
                  >
                    Log me in
                    <v-icon> mdi-arrow_right_alt</v-icon>
                  </v-btn>
                  <v-btn
                      class="btn-text"
                      color="primary"
                      data-testid="v-btn"
                      outlined
                      to="/signup"
                      width="46%"
                  >
                    Sign up now
                    <v-icon> mdi-arrow_right_alt</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </form>
            <v-row class="mt-3 mx-0 px-0">
              <v-col class="d-flex flex-column align-stretch mx-0 px-0" cols="12">
                <p class="subtitle-1">Save some time,</p>
                <GoogleSignInButton />
              </v-col>
            </v-row>
          </div>
        </v-container>
</template>

<script>
import linkedin from "@/assets/linkedin.png";
import twitter from "@/assets/twitter.png";
import Logo from "@/assets/guiomentorLogo.png";
import {email, minLength, required} from "vuelidate/lib/validators";
import {mapMutations} from "vuex";
import GoogleSignInButton from "@/components/GoogleSignInButton";


export default {
  components: {GoogleSignInButton},
  data() {
    return {
      Logo,
      linkedin,
      twitter,
      email: "",
      password: "",
      showPassword: false,
      loading: false,
      accountTypes: ["student", "teacher", "parent"],
      routeOnLogin: "/home",
      description: "Guiomentor is a platform directed to a niche of 10 to 25 years old. Its objective is to help students to choose the best career or discipline for them according to their skills and part of interest "

    };
  },
  metaInfo() {
    return {
      title: 'Guiomentor - Log In',
      url: window.location.href,
      meta: [
        {name: 'title', content: 'Guiomentor - Log In'},
        {
          name: 'description',
          content: this.description
        },
        //Facebook
        {name: 'og:type', content: 'Website'},
        {name: 'og:title', content: "Guiomentor - Log In"},
        {name: 'og:description', content: this.description},
        {name: 'og:image', content: this.Logo},

        // Twitter
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:url', content: window.location.href},
        {name: 'twitter:title', content: "Guiomentor - Log In"},
        {name: 'twitter:description', content: this.description},
        {name: 'twitter:image', content: this.Logo},
      ]
    }
  },
  methods: {
    ...mapMutations({
      SET_AUTH_SUCCESS: "auth/SET_AUTH_SUCCESS",
      SET_USER_PROFILE: "auth/SET_USER_PROFILE",
      SET_LOADING: "loading/SET_LOADING",
    }),

    emailOnChange() {
      this.$v.email.$touch();
      this.emailTaken = false;
    },

    async signIn() {
      try {
        this.loading = true;
        const body = {email: this.email, password: this.password};
        const {data} = await this.axios.post('users/login/', body, data);

        const user = data.data
        this.SET_AUTH_SUCCESS(user);
        this.SET_USER_PROFILE(user.profile);
        localStorage.setItem("access_token", user.tokens.access);
        localStorage.setItem("refresh_token", user.tokens.refresh);

        this.$analytics.logEvent("user_sign_in", {
          user: user,
          time: new Date(),
          url: window.location.href,
          version: "frontend version"
        });

        const from = this.$router.history.current.query?.from
        if (from === '/forgot_password' || from === '/signup' || from === '/on-boarding') {
          await this.$router.push(this.routeOnLogin);
        }
        else await this.$router.push(from ? from : this.routeOnLogin);

      } catch (error) {
        this.$snackbar.showMessage({
          content: this.constructSignInErrorMessage(error) || this.$t('errors.somethingWentWrongError'),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    constructSignInErrorMessage(error) {
      if (error.error) return error.error.replace(/_/g, ' ')
      let errorMessage = '';
      const errorData = error.response?.data || ''
      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.data?.non_field_errors) {
          const errors = errorData.data.non_field_errors
          if (errors.length === 1) return errorMessage = errors[0]
          errors.forEach((msg, index) => {
            if (index === errors.length - 1) {
              errorMessage += msg
            }
            errorMessage += msg + ', '
          })
        }
      }
      return errorMessage
    },
  },
  computed: {
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email &&
      errors.push(this.$t("auth.forms.errors.emailMustBeValid"));
      !this.$v.email.required &&
      errors.push(this.$t("auth.forms.errors.emailRequired"));
      this.emailTaken &&
      errors.push(this.$t("errors.emailTaken", {email: this.email}));
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      !this.$v.password.required &&
      errors.push(this.$t("auth.forms.errors.passwordRequired"));
      !this.$v.password.min &&
      errors.push(this.$t("auth.forms.errors.passwordLength"));
      return errors;
    }
  },
  validations: {
    email: {
      required, email
    },
    password: {
      required, min: minLength(6)
    },
  }
}
;
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables";

.sign-in-container {
  height: 100vh;

  .row {
    margin: 0;
  }
}

.left {
  background: #0033AA;
  height: 100vh;
  margin-top: 0;
  margin-bottom: 0;

  .logo {
    width: 130px;
    height: 130px;
  }

  .mini-column {
    elevation: higher;
    background: linear-gradient(to right, #b7edeb, #54ded2);
    min-height: 70%;
    max-width: 95%;
    border-radius: 5px;
  }
}

.right {
  background: #f5f6f8;
  height: 100vh;
}

.form {
  background: #ffffff;
  min-height: 450px;
  width: 85%;
  border-radius: 10px;
  padding: 35px;
  box-shadow:0px 42px 100px rgba(0, 0, 0, 0.12);

  .forgot-password {
    color: rgba(0, 0, 0, 0.6);
  }

  .v-btn {
    text-transform: none !important;
  }

  .login-linked-in {
    background: #0033AA !important;
    color: #ffffff !important;
  }

  .login-twitter {
    background: #2575b3 !important;
    color: #ffffff !important;;
  }

  .social-img {
    width: 20px;
    height: 80%;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .form {
    width: 100% !important;
  }
}
</style>
